// JOBE-0098.3: autocomplete options for the cursor-token. Sourced from
// caller-supplied registries: the host page passes `roster` (humans +
// agents), `entityTypes`, and `collections` via props. The palette
// reads the cursor-token from the last predicate in the parsed query.

function rosterToOptions(roster) {
  if (!roster) return [];
  const humans = (roster.humans || []).map((h) => ({
    value: h.urn,
    label: h.displayName || h.urn,
  }));
  const agents = (roster.agents || []).map((a) => ({
    value: a.urn,
    label: a.displayName || a.urn,
  }));
  return [...humans, ...agents];
}

function entityTypesToOptions(types) {
  if (!Array.isArray(types)) return [];
  return types.map((t) => ({ value: t.kind, label: t.displayName || t.kind }));
}

function kindsToOptions(kinds) {
  if (!Array.isArray(kinds)) return [];
  return kinds.map((k) => ({ value: k.kind, label: k.displayName || k.kind }));
}

function collectionsToOptions(collections) {
  if (!Array.isArray(collections)) return [];
  return collections.map((c) => ({ value: c.slug || c.id, label: c.name || c.slug || c.id }));
}

// optionsForPredicate: pick the registry that matches the current
// friendly token. Returns a (possibly filtered) option list.
//
// PR #700 review (MEDIUM): the `type:` predicate concatenates
// `sources.entityTypes` (top-level shapes — `document`, `project`, …)
// and `sources.kinds` (document sub-kinds — `briefing`, `memo`, …),
// matching what `TypeValuePopover` shows in the filter composer. Before
// this fix the palette only saw entityTypes, so `type:briefing`
// silently rendered "enter a value" while the composer offered
// `briefing` correctly. Duplicates by `value` are de-duplicated;
// entity-types win on collision since they're the primary registry.
export function optionsForPredicate(token, partial, sources) {
  const lower = (partial || '').toLowerCase();
  let pool = [];
  if (['owner', 'by', 'creator', 'watcher'].includes(token)) {
    pool = rosterToOptions(sources.roster);
  } else if (token === 'type') {
    const typeOpts = entityTypesToOptions(sources.entityTypes);
    const kindOpts = kindsToOptions(sources.kinds);
    const seen = new Set(typeOpts.map((o) => o.value));
    pool = [...typeOpts, ...kindOpts.filter((o) => !seen.has(o.value))];
  } else if (token === 'in') {
    pool = collectionsToOptions(sources.collections);
  } else {
    return [];
  }
  if (!lower) return pool;
  return pool.filter(
    (o) => o.label.toLowerCase().includes(lower) || o.value.toLowerCase().includes(lower),
  );
}

export function AutocompleteSection({
  cursorPredicate,
  partial,
  sources,
  highlightIndex,
  onPick,
  onHighlight,
}) {
  if (!cursorPredicate) return null;
  const options = optionsForPredicate(cursorPredicate.friendlyToken, partial, sources);
  if (options.length === 0) {
    return (
      <section className="omni-section omni-section-autocomplete">
        <h4 className="omni-section-title">{cursorPredicate.friendlyToken}</h4>
        <p className="omni-section-empty">enter a value</p>
      </section>
    );
  }
  return (
    <section className="omni-section omni-section-autocomplete" aria-label="Autocomplete">
      <h4 className="omni-section-title">{cursorPredicate.friendlyToken}</h4>
      <ul className="omni-opt-list" role="listbox">
        {options.map((opt, i) => {
          const isHigh = i === highlightIndex;
          return (
            <li key={opt.value}>
              <button
                type="button"
                className={isHigh ? 'omni-opt omni-opt-high' : 'omni-opt'}
                onMouseEnter={() => onHighlight && onHighlight(i)}
                onClick={() => onPick && onPick(opt, i)}
                role="option"
                aria-selected={isHigh}
                data-option-value={opt.value}
              >
                <span className="omni-opt-label">{opt.label}</span>
                <code className="omni-opt-value">{opt.value}</code>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
